import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    MagnifyingGlassIcon,
    DocumentTextIcon,
    EyeIcon,
    HeartIcon,
    ShareIcon,
    CalendarIcon,
    TagIcon,
    PhotoIcon
} from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import { blogAPI } from '../../services/api'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ErrorMessage from '../../components/Common/ErrorMessage'
import BlogForm from '../../components/Admin/BlogForm'
import PermissionWrapper from '../../components/Admin/PermissionWrapper'
import toast from 'react-hot-toast'

const AdminBlogs = () => {
    return (
        <PermissionWrapper requiredPermission="blogs">
            <AdminBlogsContent />
        </PermissionWrapper>
    )
}

const AdminBlogsContent = () => {
    const [filters, setFilters] = useState({
        search: '',
        category: '',
        status: '',
        featured: 'all',
        page: 1,
        limit: 10,
        sort: 'newest'
    })
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editingBlog, setEditingBlog] = useState(null)
    const [showCreateForm, setShowCreateForm] = useState(false)
    const queryClient = useQueryClient()

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['admin-blogs', filters],
        queryFn: () => blogAPI.getAdminBlogs(filters),
        keepPreviousData: true,
    })

    const createMutation = useMutation({
        mutationFn: blogAPI.createBlog,
        onSuccess: () => {
            toast.success('Blog created successfully!')
            setIsModalOpen(false)
            setEditingBlog(null)
            setShowCreateForm(false)
            queryClient.invalidateQueries(['admin-blogs'])
            queryClient.invalidateQueries(['blogs'])
            queryClient.invalidateQueries(['featured-blogs'])
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to create blog')
        }
    })

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => blogAPI.updateBlog(id, data),
        onSuccess: () => {
            toast.success('Blog updated successfully!')
            setIsModalOpen(false)
            setEditingBlog(null)
            setShowCreateForm(false)
            queryClient.invalidateQueries(['admin-blogs'])
            queryClient.invalidateQueries(['blogs'])
            queryClient.invalidateQueries(['featured-blogs'])
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to update blog')
        }
    })

    const deleteMutation = useMutation({
        mutationFn: blogAPI.deleteBlog,
        onSuccess: () => {
            toast.success('Blog deleted successfully!')
            queryClient.invalidateQueries(['admin-blogs'])
            queryClient.invalidateQueries(['blogs'])
            queryClient.invalidateQueries(['featured-blogs'])
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to delete blog')
        }
    })

    const statusMutation = useMutation({
        mutationFn: ({ id, status }) => blogAPI.updateBlogStatus(id, status),
        onSuccess: () => {
            toast.success('Blog status updated successfully!')
            queryClient.invalidateQueries(['admin-blogs'])
            queryClient.invalidateQueries(['blogs'])
            queryClient.invalidateQueries(['featured-blogs'])
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to update blog status')
        }
    })

    const featuredMutation = useMutation({
        mutationFn: blogAPI.toggleFeatured,
        onSuccess: () => {
            toast.success('Blog featured status updated!')
            queryClient.invalidateQueries(['admin-blogs'])
            queryClient.invalidateQueries(['blogs'])
            queryClient.invalidateQueries(['featured-blogs'])
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to update featured status')
        }
    })

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1
        }))
    }

    const handleSearch = (e) => {
        e.preventDefault()
        refetch()
    }

    const handleCreate = () => {
        setEditingBlog(null)
        setShowCreateForm(true)
        setIsModalOpen(true)
    }

    const handleEdit = (blog) => {
        setEditingBlog(blog)
        setShowCreateForm(true)
        setIsModalOpen(false) // Use full-page form for editing
    }

    const handleDelete = (blog) => {
        if (window.confirm(`Are you sure you want to delete "${blog.title}"?`)) {
            deleteMutation.mutate(blog._id)
        }
    }

    const handleStatusChange = (blog, newStatus) => {
        statusMutation.mutate({ id: blog._id, status: newStatus })
    }

    const handleFeaturedToggle = (blog) => {
        featuredMutation.mutate(blog._id)
    }

    const handleSubmit = (formData) => {
        if (editingBlog) {
            updateMutation.mutate({ id: editingBlog._id, data: formData })
        } else {
            createMutation.mutate(formData)
        }
    }

    const getStatusBadge = (status) => {
        const statusConfig = {
            draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
            published: { color: 'bg-green-100 text-green-800', label: 'Published' },
            archived: { color: 'bg-yellow-100 text-yellow-800', label: 'Archived' }
        }
        const config = statusConfig[status] || statusConfig.draft
        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                {config.label}
            </span>
        )
    }

    const getCategoryBadge = (category) => {
        return (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {category}
            </span>
        )
    }

    if (isLoading) return <LoadingSpinner />
    if (error) return <ErrorMessage message="Failed to load blogs" />

    const blogs = data?.data?.blogs || []
    const pagination = data?.data?.pagination || {}

    // Show create form if explicitly requested
    if (showCreateForm) {
        return (
            <div className="min-h-screen bg-gray-50">
                <Helmet>
                    <title>Create Blog - Admin Panel</title>
                </Helmet>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="mb-8">
                        <button
                            onClick={() => setShowCreateForm(false)}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                            ← Back to Blog List
                        </button>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">
                                {editingBlog ? 'Edit Blog' : 'Create New Blog'}
                            </h2>
                            <p className="text-gray-600 mt-2">
                                {editingBlog
                                    ? `Edit the details of "${editingBlog.title}"`
                                    : 'Fill in the details to create a new blog post'
                                }
                            </p>
                        </div>
                        <BlogForm
                            blog={editingBlog}
                            onSubmit={handleSubmit}
                            onClose={() => {
                                setShowCreateForm(false)
                                setEditingBlog(null)
                            }}
                            isLoading={editingBlog ? updateMutation.isLoading : createMutation.isLoading}
                            isModal={false}
                        />
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Helmet>
                <title>Blog Management - Admin Panel</title>
            </Helmet>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Blog Management</h1>
                            <p className="mt-2 text-gray-600">Manage your blog posts and content</p>
                        </div>
                        <button
                            onClick={handleCreate}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            <PlusIcon className="h-5 w-5 mr-2" />
                            Create Blog
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                    placeholder="Search blogs..."
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                />
                                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                            <select
                                value={filters.category}
                                onChange={(e) => handleFilterChange('category', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            >
                                <option value="">All Categories</option>
                                <option value="Education News">Education News</option>
                                <option value="Career Guidance">Career Guidance</option>
                                <option value="Study Tips">Study Tips</option>
                                <option value="University Updates">University Updates</option>
                                <option value="Admission Process">Admission Process</option>
                                <option value="Scholarships">Scholarships</option>
                                <option value="Exam Preparation">Exam Preparation</option>
                                <option value="Industry Insights">Industry Insights</option>
                                <option value="Student Success Stories">Student Success Stories</option>
                                <option value="Technology in Education">Technology in Education</option>
                                <option value="Online Learning">Online Learning</option>
                                <option value="International Education">International Education</option>
                                <option value="Skill Development">Skill Development</option>
                                <option value="Job Market Trends">Job Market Trends</option>
                                <option value="Higher Education">Higher Education</option>
                                <option value="Research Opportunities">Research Opportunities</option>
                                <option value="Campus Life">Campus Life</option>
                                <option value="Alumni Stories">Alumni Stories</option>
                                <option value="Education Policy">Education Policy</option>
                                <option value="Learning Resources">Learning Resources</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                            <select
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            >
                                <option value="">All Status</option>
                                <option value="draft">Draft</option>
                                <option value="published">Published</option>
                                <option value="archived">Archived</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Featured</label>
                            <select
                                value={filters.featured}
                                onChange={(e) => handleFilterChange('featured', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            >
                                <option value="all">All Blogs</option>
                                <option value="true">Featured Only</option>
                                <option value="false">Non-Featured</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                            <select
                                value={filters.sort}
                                onChange={(e) => handleFilterChange('sort', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            >
                                <option value="newest">Newest First</option>
                                <option value="title">Title A-Z</option>
                                <option value="views">Most Viewed</option>
                            </select>
                        </div>
                    </form>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <DocumentTextIcon className="h-8 w-8 text-blue-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-500">Total Blogs</p>
                                <p className="text-2xl font-semibold text-gray-900">{pagination.total_count || 0}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <EyeIcon className="h-8 w-8 text-green-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-500">Published</p>
                                <p className="text-2xl font-semibold text-gray-900">
                                    {blogs.filter(blog => blog.status === 'published').length}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <HeartIcon className="h-8 w-8 text-red-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-500">Total Shares</p>
                                <p className="text-2xl font-semibold text-gray-900">
                                    {blogs.reduce((sum, blog) => sum + (blog.shares || 0), 0)}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ShareIcon className="h-8 w-8 text-purple-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-500">Total Views</p>
                                <p className="text-2xl font-semibold text-gray-900">
                                    {blogs.reduce((sum, blog) => sum + (blog.views || 0), 0)}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Blogs Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">Blogs List</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Blog
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Category
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Stats
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Date
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {blogs.map((blog) => (
                                    <motion.tr
                                        key={blog._id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="hover:bg-gray-50"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-12 w-12">
                                                    {blog.featured_image ? (
                                                        <img
                                                            className="h-12 w-12 rounded-lg object-cover"
                                                            src={blog.featured_image}
                                                            alt={blog.title}
                                                        />
                                                    ) : (
                                                        <div className="h-12 w-12 rounded-lg bg-gray-200 flex items-center justify-center">
                                                            <PhotoIcon className="h-6 w-6 text-gray-400" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {blog.title}
                                                        {blog.featured && (
                                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                                                Featured
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-gray-500 truncate max-w-xs">
                                                        {blog.excerpt}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {getCategoryBadge(blog.category)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {getStatusBadge(blog.status)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div className="flex items-center space-x-4">
                                                <div className="flex items-center">
                                                    <EyeIcon className="h-4 w-4 mr-1" />
                                                    {blog.views || 0}
                                                </div>
                                                <div className="flex items-center">
                                                    <ShareIcon className="h-4 w-4 mr-1" />
                                                    {blog.shares || 0}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div className="flex items-center">
                                                <CalendarIcon className="h-4 w-4 mr-1" />
                                                {new Date(blog.createdAt).toLocaleDateString()}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center justify-end space-x-2">
                                                {blog.status === 'published' && (
                                                    <Link
                                                        to={`/blogs/${blog.slug}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-green-600 hover:text-green-900"
                                                        title="View Blog"
                                                    >
                                                        <EyeIcon className="h-4 w-4" />
                                                    </Link>
                                                )}
                                                <button
                                                    onClick={() => handleEdit(blog)}
                                                    className="text-indigo-600 hover:text-indigo-900"
                                                    title="Edit Blog"
                                                >
                                                    <PencilIcon className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleFeaturedToggle(blog)}
                                                    className={`${blog.featured ? 'text-yellow-600 hover:text-yellow-900' : 'text-gray-400 hover:text-yellow-600'}`}
                                                    title={blog.featured ? 'Remove from Featured' : 'Mark as Featured'}
                                                >
                                                    <TagIcon className="h-4 w-4" />
                                                </button>
                                                <select
                                                    value={blog.status}
                                                    onChange={(e) => handleStatusChange(blog, e.target.value)}
                                                    className="text-xs border border-gray-300 rounded px-2 py-1"
                                                >
                                                    <option value="draft">Draft</option>
                                                    <option value="published">Published</option>
                                                    <option value="archived">Archived</option>
                                                </select>
                                                <button
                                                    onClick={() => handleDelete(blog)}
                                                    className="text-red-600 hover:text-red-900"
                                                    title="Delete Blog"
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {pagination.total_pages > 1 && (
                        <div className="px-6 py-4 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-gray-700">
                                    Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                                    {Math.min(pagination.current_page * pagination.per_page, pagination.total_count)} of{' '}
                                    {pagination.total_count} results
                                </div>
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => handleFilterChange('page', pagination.current_page - 1)}
                                        disabled={pagination.current_page <= 1}
                                        className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                    >
                                        Previous
                                    </button>
                                    <span className="px-3 py-1 text-sm bg-primary-600 text-white rounded-md">
                                        {pagination.current_page}
                                    </span>
                                    <button
                                        onClick={() => handleFilterChange('page', pagination.current_page + 1)}
                                        disabled={pagination.current_page >= pagination.total_pages}
                                        className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Blog Form Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <BlogForm
                        blog={editingBlog}
                        onSubmit={handleSubmit}
                        onClose={() => {
                            setIsModalOpen(false)
                            setEditingBlog(null)
                            setShowCreateForm(false)
                        }}
                        isLoading={createMutation.isLoading || updateMutation.isLoading}
                        isModal={true}
                    />
                )}
            </AnimatePresence>
        </div>
    )
}

export default AdminBlogs
